import { safeRead, safeRemove, safeWrite } from '@utils/session-storage.utils'

// Cross-page signal: "user just completed a recovery flow and is now
// landing on /settings". Kratos's recovery flow ends with a 303 to settings
// with no URL marker, so settings can't otherwise distinguish this entry
// from a direct visit.
//
// Don't reach for document.referrer here — Referrer-Policy: no-referrer is
// set in config/server/security.js, so the referrer is always empty.
//
// The {at} timestamp drives a soft TTL: a wrong-code submit keeps the user
// on /recovery without consuming the flag, and a visit to /settings hours
// later via cockpit menu would otherwise still force Password-tab. The 303
// itself completes in well under a second, so anything older than 60s is
// overwhelmingly orphaned.

const JUST_COMPLETED_KEY = 'paretos.authui.recovery.justCompleted'
const RETURN_TO_KEY = 'paretos.authui.recovery.returnTo'
const HANDOFF_TTL_MS = 60_000
// Recovery flow lifespan is 1h in kratos.yaml — the stash must outlive a
// wrong-password retry, but expire well before a same-tab visit hours later
// to /settings via cockpit menu can spuriously redirect somewhere else.
const RETURN_TO_TTL_MS = 60 * 60_000

interface HandoffPayload {
  at: number
}

interface ReturnToPayload {
  url: string
  at: number
}

const parsePayload = (raw: string | null): HandoffPayload | null => {
  if (raw === null) {
    return null
  }
  // Old codepath wrote a bare '1' with no timestamp. Treat as expired so
  // a stale tab still holding it after a rolling deploy can't force
  // Password-tab hours later. Cost: a user mid-deploy who just completed
  // recovery loses the one-shot default once.
  // TODO(2026-07-15): drop this branch — sessionStorage clears on tab
  // close, and ~8 weeks after the 0.5.1 deploy any tab still holding a
  // bare '1' is overwhelmingly long-stale anyway.
  if (raw === '1') {
    return null
  }
  try {
    const parsed: unknown = JSON.parse(raw)
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      'at' in parsed &&
      typeof (parsed as { at: unknown }).at === 'number'
    ) {
      return { at: (parsed as HandoffPayload).at }
    }
  } catch {
    // Malformed → treat as absent.
  }
  return null
}

export const markRecoveryJustCompleted = (): void => {
  safeWrite(JUST_COMPLETED_KEY, JSON.stringify({ at: Date.now() }))
}

// Pure read — clearing lives in clearRecoveryJustCompleted so the lazy
// useState initializer that calls this stays free of side effects. React 18
// StrictMode runs lazy initializers twice in dev; a consume-on-read would
// clear on the first pass and lose the signal on the second.
export const peekRecoveryJustCompleted = (): boolean => {
  const payload = parsePayload(safeRead(JUST_COMPLETED_KEY))
  if (payload === null) {
    return false
  }
  // Bound both ends: a same-origin script writing `{at: MAX_SAFE_INTEGER}`
  // (or even just a clock skew putting `at` in the future) would otherwise
  // produce a negative age that's always < TTL and keep the flag eternally
  // "fresh". Strict-positive age guards the in-future case too.
  const age = Date.now() - payload.at
  return age >= 0 && age < HANDOFF_TTL_MS
}

// Idempotent — StrictMode dev runs the consuming useEffect twice.
export const clearRecoveryJustCompleted = (): void => {
  safeRemove(JUST_COMPLETED_KEY)
}

// Kratos /admin/recovery/code (customer-api invitation path) doesn't accept
// return_to, so the URL param is dropped before /settings sees it.
// RecoveryPage stashes here on code-stage submit; SettingsPage falls back
// to this when flow.return_to is null. TTL outlives wrong-password retries.
export const stashRecoveryReturnTo = (url: string): void => {
  if (!url) {
    return
  }
  safeWrite(RETURN_TO_KEY, JSON.stringify({ url, at: Date.now() }))
}

const parseReturnToPayload = (raw: string | null): ReturnToPayload | null => {
  if (raw === null) {
    return null
  }
  try {
    const parsed: unknown = JSON.parse(raw)
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      'url' in parsed &&
      'at' in parsed &&
      typeof (parsed as { url: unknown }).url === 'string' &&
      typeof (parsed as { at: unknown }).at === 'number'
    ) {
      return parsed as ReturnToPayload
    }
  } catch {
    // Malformed → treat as absent.
  }
  return null
}

export const peekRecoveryReturnTo = (): string | null => {
  const payload = parseReturnToPayload(safeRead(RETURN_TO_KEY))
  if (payload === null) {
    return null
  }
  const age = Date.now() - payload.at
  if (age < 0 || age >= RETURN_TO_TTL_MS) {
    return null
  }
  return payload.url
}

export const clearRecoveryReturnTo = (): void => {
  safeRemove(RETURN_TO_KEY)
}
